if (!customElements.get('cart-review-slider')) {
  customElements.define(
    'cart-review-slider',
    class CartReviewSlider extends HTMLElement {
      // Shared across instances: the drawer re-renders its markup after
      // every cart change, and the slider should not jump back to the
      // first review each time.
      static currentIndex = 0;

      static AUTOPLAY_DELAY = 6000;

      connectedCallback() {
        this.track = this.querySelector('[data-slider-track]');
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.dots = [...this.querySelectorAll('[data-slider-dot]')];

        if (!this.track || this.slides.length < 2) return;

        this.dots.forEach((dot, index) =>
          dot.addEventListener('click', () => {
            this.goTo(index);
            this.restart();
          }),
        );

        this.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = requestAnimationFrame(() =>
              this.setActive(this.indexFromScroll()),
            );
          },
          { passive: true },
        );

        // Pause while the visitor reads or swipes; resume when they leave.
        this.addEventListener('pointerenter', () => this.pause());
        this.addEventListener('pointerleave', () => this.play());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.play());
        this.track.addEventListener('touchstart', () => this.pause(), {
          passive: true,
        });

        this.goTo(CartReviewSlider.currentIndex % this.slides.length, 'instant');
        this.play();
      }

      disconnectedCallback() {
        this.pause();
        cancelAnimationFrame(this.scrollFrame);
      }

      get canAutoplay() {
        return (
          this.hasAttribute('data-autoplay') &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      }

      // Only advance while this copy is on screen: the drawer renders a
      // sticky and an inline copy (one of them hidden), may be closed, and
      // the tab may be in the background.
      get isShown() {
        const drawer = this.closest('cart-drawer');
        return (
          document.visibilityState === 'visible' &&
          this.offsetParent !== null &&
          (!drawer || drawer.classList.contains('active'))
        );
      }

      indexFromScroll() {
        const width = this.track.clientWidth || 1;
        return Math.min(
          this.slides.length - 1,
          Math.max(0, Math.round(this.track.scrollLeft / width)),
        );
      }

      goTo(index, behavior = 'smooth') {
        const slide = this.slides[index];
        if (!slide) return;

        // Update the dots right away; the scroll listener keeps them in sync
        // for swipes, where no target index is known up front.
        this.setActive(index);
        this.track.scrollTo({ left: slide.offsetLeft, behavior });
      }

      setActive(index) {
        CartReviewSlider.currentIndex = index;
        this.dots.forEach((dot, dotIndex) => {
          if (dotIndex === index) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      next() {
        if (!this.isShown) return;

        const index = (this.indexFromScroll() + 1) % this.slides.length;
        this.goTo(index);
      }

      play() {
        if (!this.canAutoplay) return;

        this.pause();
        this.timer = setInterval(
          () => this.next(),
          CartReviewSlider.AUTOPLAY_DELAY,
        );
      }

      pause() {
        clearInterval(this.timer);
      }

      restart() {
        if (this.matches(':hover') || this.contains(document.activeElement))
          return;

        this.play();
      }
    },
  );
}
